import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CountryFlag from '../../components/common/countryFlag';
import { TrackingResponseDto } from '../../types/types';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';

export interface ProfileStatsSectionProps {
  stats: TrackingResponseDto | null;
  displayedProfileViews: number;
  profileRange: '7days' | 'month' | 'all';
  onProfileRangeChange: (range: '7days' | 'month' | 'all') => void;
  isLoading?: boolean;
}

type CountryView = { country: string; views: number };

// Same order as Figma: All | 7 days | 1 Month
const RANGE_OPTIONS = [
  { value: 'all', label: 'all' },
  { value: '7days', label: 'seven_days' },
  { value: 'month', label: 'one_month' },
] as const;

// The tracking API sends country codes (e.g. "IN"), CountryFlag expects names (e.g. "India")
const getCountryName = (country: string): string => {
  if (!/^[A-Za-z]{2}$/.test(country)) return country;
  try {
    return (
      new (Intl as any).DisplayNames(['en'], { type: 'region' }).of(
        country.toUpperCase()
      ) || country
    );
  } catch {
    return country;
  }
};

const CountryBars: React.FC<{
  data: CountryView[];
  activeCountry?: string;
  onSelect: (country: string) => void;
}> = ({ data, activeCountry, onSelect }) => {
  const { t } = useTranslation('dashboard');
  const [animate, setAnimate] = useState(false);

  // Bars grow from 0 to their height whenever the data changes
  useEffect(() => {
    setAnimate(false);
    const timer = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(timer);
  }, [data]);

  const maxViews = Math.max(...data.map((item) => item.views), 1);
  const totalViews = data.reduce((sum, item) => sum + item.views, 0);

  return (
    <div className="flex w-full justify-start gap-4 overflow-x-auto overflow-y-hidden pb-1">
      {data.map((item, index) => {
        const countryName = item.country
          ? getCountryName(item.country)
          : t('not_specified', { defaultValue: 'Not specified' });
        const percentage =
          totalViews > 0
            ? ((item.views / totalViews) * 100).toFixed(1)
            : '0';
        const height = maxViews > 0 ? (item.views / maxViews) * 100 : 0;
        const isActive = item.country === activeCountry;

        return (
          <button
            key={`${item.country}-${index}`}
            type="button"
            onClick={() => onSelect(item.country)}
            title={`${countryName}: ${item.views} (${percentage}%)`}
            className="group flex min-w-10 max-w-16 flex-1 flex-col items-center gap-2 cursor-pointer transition-transform hover:scale-105"
          >
            <div className="flex h-36 w-full items-end">
              <div
                className={`w-full rounded-lg transition-all duration-500 ease-out ${
                  isActive
                    ? 'bg-blue-600 shadow-md'
                    : 'bg-gray-200 group-hover:bg-gray-300'
                }`}
                style={{ height: animate ? `${Math.max(height, 4)}%` : '0%' }}
              />
            </div>

            {item.country ? (
              <CountryFlag countryName={countryName} size={16} />
            ) : (
              <span className="max-w-full truncate text-[10px] font-medium text-gray-500">
                {countryName}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Dedicated Section 1: Profile Views by Country
 */
export const ProfileStatsSection: React.FC<ProfileStatsSectionProps> = ({
  stats,
  displayedProfileViews,
  profileRange,
  onProfileRangeChange,
  isLoading = false,
}) => {
  const { t } = useTranslation('dashboard');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const profileCountryViews: CountryView[] = stats?.countryViews ?? [];
  const activeCountry =
    profileCountryViews.find((item) => item.country === selectedCountry) ??
    profileCountryViews.reduce<CountryView | undefined>(
      (top, item) => (!top || item.views > top.views ? item : top),
      undefined
    );

  return (
    <div className="col-span-3 h-full">
      <div className="flex h-full flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
        <h3 className="text-sm font-medium leading-5 text-gray-900">
          {t('profile_views_by_country', {
            defaultValue: 'Profile views',
          })}
        </h3>

        {/* Range switch */}
        <div className="inline-flex w-fit items-center gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
          {RANGE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => onProfileRangeChange(value)}
              className={`rounded-3xl px-3 py-1.5 text-sm font-medium leading-5 transition-colors ${
                profileRange === value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>

        {/* Total views + views of the selected country bar */}
        <div className="flex gap-4">
          <div className="w-36">
            <div className="text-xs font-medium text-gray-500">
              {t('total_profile_views', {
                defaultValue: 'Total Profile Views',
              })}
            </div>
            {isLoading && !stats ? (
              <Skeleton className="mt-1.5 h-6 w-12" />
            ) : (
              <div className="text-xl font-bold text-gray-900 mt-0.5">
                {displayedProfileViews}
              </div>
            )}
          </div>

          {activeCountry && (
            <div className="w-36">
              <div className="text-xs font-medium text-gray-500 truncate">
                {activeCountry.country
                  ? getCountryName(activeCountry.country)
                  : t('views', { defaultValue: 'Views' })}
              </div>
              <div className="text-xl font-bold text-blue-600 mt-0.5">
                {activeCountry.views}
              </div>
            </div>
          )}
        </div>

        {/* Country Chart */}
        <div className="flex min-h-[190px] items-center justify-center pt-2">
          {isLoading && !stats ? (
            <div role="status" aria-busy="true" className="flex h-36 w-full items-end gap-4">
              {['h-20', 'h-36', 'h-12', 'h-28', 'h-16'].map((heightClass) => (
                <Skeleton key={heightClass} className={`flex-1 rounded-lg ${heightClass}`} />
              ))}
            </div>
          ) : profileCountryViews.length > 0 ? (
            <CountryBars
              data={profileCountryViews}
              activeCountry={selectedCountry ?? activeCountry?.country}
              onSelect={setSelectedCountry}
            />
          ) : (
            <EmptyState text={t('no_data_to_display')} />
          )}
        </div>
      </div>
    </div>
  );
};
